import { useCallback, useEffect, useMemo, useState } from 'react';
import { Form, FormProps, FormRenderProps } from 'react-final-form';
import { Box, Grid } from '@mui/material';

import { Button, Typography } from '@occc/ui-design-system';
import { KeyboardDatePicker, Select } from '@occc-app/design-system';
import { getDateRanges, RangeType } from '@occc/ui-design-system';

type RangeTypeAndCustom = RangeType | 'custom';

const selectInputItems: Array<{ label: string; value: RangeTypeAndCustom }> = [
  {
    label: 'Custom Range',
    value: 'custom',
  },
  {
    label: 'Last 30 days',
    value: 'last_30_days',
  },
  {
    label: 'Last 60 days',
    value: 'last_60_days',
  },
];

export type TobaccoWeeklySubmissionsFilterFormValues = {
  submittedAtFrom?: Date;
  submittedAtTo?: Date;
};

export type TobaccoWeeklySubmissionsFilterFormProps = {
  onSubmit: FormProps<TobaccoWeeklySubmissionsFilterFormValues>['onSubmit'];
  onReset: () => void;
  initialValues: TobaccoWeeklySubmissionsFilterFormValues;
};

export const TobaccoWeeklySubmissionsFilterForm = ({
  onSubmit,
  onReset,
  initialValues,
}: TobaccoWeeklySubmissionsFilterFormProps) => {
  const [currentRange, setCurrentRange] = useState<RangeTypeAndCustom>('last_30_days');
  const ranges = useMemo(() => getDateRanges(), []);

  useEffect(() => {
    if (!initialValues.submittedAtFrom || !initialValues.submittedAtTo) {
      return setCurrentRange('custom');
    }
    if (
      ranges.last_30_days.isSelected(initialValues.submittedAtFrom, initialValues.submittedAtTo)
    ) {
      return setCurrentRange('last_30_days');
    }
    if (
      ranges.last_60_days.isSelected(initialValues.submittedAtFrom, initialValues.submittedAtTo)
    ) {
      return setCurrentRange('last_60_days');
    }
    setCurrentRange('custom');
  }, [initialValues, ranges.last_30_days, ranges.last_60_days]);

  const renderForm: FormRenderProps<TobaccoWeeklySubmissionsFilterFormValues>['render'] =
    useCallback(
      ({
        handleSubmit,
        form,
        values,
      }: FormRenderProps<TobaccoWeeklySubmissionsFilterFormValues>) => {
        const reset = () => {
          form.reset();
          onReset();
        };

        return (
          <form onSubmit={handleSubmit}>
            <Box p={3}>
              <Typography variant='h6' mb={3}>
                Filter Store Submissions
              </Typography>

              <Select
                name='submittedAtRange'
                label='Submission Date'
                data={selectInputItems}
                fieldProps={{ initialValue: currentRange }}
                onChange={(event) => {
                  const range = (event.target as { value: RangeTypeAndCustom }).value;
                  if (range === 'custom') {
                    form.change('submittedAtFrom', undefined);
                    form.change('submittedAtTo', undefined);
                  } else {
                    form.change('submittedAtFrom', ranges[range].startDate);
                    form.change('submittedAtTo', ranges[range].endDate);
                  }
                  setCurrentRange(range);
                }}
              />

              {currentRange === 'custom' && (
                <Grid container spacing={3} mt={1}>
                  <Grid item xs={12} sm={6}>
                    <KeyboardDatePicker
                      label='Start Date'
                      name='submittedAtFrom'
                      maxDate={values?.submittedAtTo}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <KeyboardDatePicker
                      label='End Date'
                      name='submittedAtTo'
                      minDate={values?.submittedAtFrom}
                    />
                  </Grid>
                </Grid>
              )}
            </Box>
            <Box display='flex' justifyContent='flex-end' gap={2} p={3} pt={0}>
              <Button size='medium' variant='text' onClick={reset}>
                Reset
              </Button>
              <Button size='medium' variant='contained' type='submit'>
                Apply
              </Button>
            </Box>
          </form>
        );
      },
      [currentRange, onReset, ranges],
    );
  return (
    <Form<TobaccoWeeklySubmissionsFilterFormValues>
      onSubmit={onSubmit}
      render={renderForm}
      initialValues={initialValues}
    />
  );
};
